import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, MapPin, Phone, User, CreditCard, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RETRIES = 20;

export default function GuestOrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryRef = useRef(null);
  const mountedRef = useRef(true);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    mountedRef.current = true;

    if (order) return;
    if (!sessionId && !orderId) {
      setError(true);
      return;
    }

    setLoading(true);
    let attempts = 0;

    const checkPayment = async () => {
      attempts++;
      try {
        let response;

        // Try session-based lookup first
        if (sessionId) {
          response = await axios.get(`${API}/payments/guest-status/${sessionId}`);
        } else if (orderId) {
          // Fallback: lookup by order_id directly
          response = await axios.get(`${API}/payments/guest-order/${orderId}`);
        }

        if (!mountedRef.current) return;

        if (response?.data?.payment_status === 'paid' && response?.data?.order) {
          setOrder(response.data.order);
          setLoading(false);
          return;
        }

        // Not paid yet — retry with backoff (max MAX_RETRIES)
        if (attempts < MAX_RETRIES) {
          setRetryCount(attempts);
          const delay = Math.min(2000 + attempts * 500, 5000);
          retryRef.current = setTimeout(checkPayment, delay);
        } else {
          // Max retries reached — try one last order lookup
          if (orderId) {
            try {
              const fallback = await axios.get(`${API}/payments/guest-order/${orderId}`);
              if (fallback?.data?.order) {
                setOrder(fallback.data.order);
                setLoading(false);
                return;
              }
            } catch {
              // ignore fallback error
            }
          }
          setLoading(false);
          setError(true);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        // On 404, try order_id fallback
        if (err.response?.status === 404 && orderId && sessionId) {
          try {
            const fallback = await axios.get(`${API}/payments/guest-order/${orderId}`);
            if (fallback?.data?.order) {
              setOrder(fallback.data.order);
              setLoading(false);
              return;
            }
          } catch {
            // ignore
          }
        }
        if (attempts < 3) {
          retryRef.current = setTimeout(checkPayment, 3000);
        } else {
          setError(true);
          setLoading(false);
        }
      }
    };

    checkPayment();

    return () => {
      mountedRef.current = false;
      if (retryRef.current) {
        clearTimeout(retryRef.current);
        retryRef.current = null;
      }
    };
  }, [sessionId, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPaidOnline = order?.payment_method === 'Carte' || order?.payment_status === 'paid';
  const showLoading = loading && !order;
  const showError = !loading && !order && error;

  return (
    <Layout showFooter={false}>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-lg mx-auto px-4 py-12">

          {showLoading && (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Confirmation du paiement en cours...</p>
                <p className="text-gray-400 text-sm mt-1">
                  {retryCount > 5
                    ? "Le paiement est en cours de traitement, merci de patienter..."
                    : "Veuillez patienter quelques instants"}
                </p>
              </div>
            </div>
          )}

          {showError && (
            <div className="text-center py-24">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Paiement en cours de verification</h1>
              <p className="text-gray-500 mb-6">
                Votre paiement a bien ete enregistre. Si vous ne voyez pas la confirmation,
                pas d'inquietude : le restaurant a recu votre commande.
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Un email de confirmation vous sera envoye sous peu.
              </p>
              <Button
                onClick={() => navigate('/restaurants')}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8"
                data-testid="back-to-restaurants-error"
              >
                Retour aux restaurants
              </Button>
            </div>
          )}

          {order && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="guest-order-confirmed">
                  Commande confirmee !
                </h1>
                <p className="text-gray-500">
                  Commande #{order.order_number}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-100 space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium">{order.guest_name}</p>
                    <p className="text-sm text-gray-500">{order.guest_phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <p className="font-medium">{order.restaurant_name}</p>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <p className="font-medium">
                    Retrait a {order.pickup_slot}
                    {order.pickup_date && ` le ${new Date(order.pickup_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                  </p>
                </div>

                <hr className="border-gray-100" />

                <div className="space-y-2">
                  {(order.items || []).map((item, idx) => (
                    <div key={item.id || `item-${idx}`} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.dish_name}</span>
                      <span className="font-medium">{(Number(item.total_price) || 0).toFixed(2)} EUR</span>
                    </div>
                  ))}
                </div>

                <hr className="border-gray-100" />

                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{(Number(order.total_amount) || 0).toFixed(2)} EUR</span>
                </div>

                {isPaidOnline ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                    <p className="text-sm text-emerald-700">Paye en ligne par carte bancaire</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <Phone className="w-4 h-4 text-amber-600" />
                    <p className="text-sm text-amber-700">Paiement sur place a la recuperation</p>
                  </div>
                )}
              </div>

              <div className="mt-6 text-center">
                <Button
                  onClick={() => navigate('/restaurants')}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8"
                  data-testid="back-to-restaurants"
                >
                  Retour aux restaurants
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
